import {Resource, PlayerSettings, OperatorSettings, ActionType, ApplicationType, License, PluginSubType, FormType, FormField, FormFieldType, TemplateType, Uid, AdultContentVisibility} from 'sdk/src/defines';
import defaultImage from '@src/assets/images/content/channel/128.jpg';
import AbstractFileModelResource from './abstract/abstract-file-model-resource';
import {AttributesType, DispatchFunc, DispatchResponse, DropDownItem, ErrorType, LocalesType} from '../utility/types';
import Form from './items/form';
import PluginInstance from './items/plugin-instance';
import Template from './items/template';
import {TFunction} from 'react-i18next';
import forms from './forms';
import templates from './templates';
import plugins, {Plugin} from './plugins';
import pluginInstances from './plugin-instances';


class Operator extends AbstractFileModelResource {
    public declare name: string;

    public declare operatorSettings?: OperatorSettings;

    public declare playerSettings?: PlayerSettings;

    public declare license?: License;

    public declare disabled?: boolean;

    public declare operatorId?: string;

    public resourceItemClass: typeof Operator = Operator;

    public resourceType: Resource = Resource.OPERATOR;

    public locales: LocalesType;

    public allowedActions: ActionType[] = [
        ActionType.UPDATE,
        ActionType.GET,
    ];

    public update(params: AttributesType): DispatchResponse {
        if (!Object.prototype.hasOwnProperty.call(params, 'license')) {
            return super.update(params);
        }

        // The submitted license is encoded text; keep the parsed active license
        // until the server has validated and saved its replacement.
        return (dispatch: DispatchFunc) => {
            dispatch({type: `${this.resourceType}_UPDATING`});
            this.apiCalls.update(undefined, params).then((data: AttributesType) => {
                dispatch(this.updated(data));
            }).catch((error: ErrorType) => {
                dispatch({type: `${this.resourceType}_UPDATE_FAILED`, error});
            });
        };
    }

    public uploadFiles(files: {[name: string]: File}): DispatchResponse {
        return async (dispatch: DispatchFunc) => {
            const names: string[] = Object.keys(files);

            const promises: Promise<any>[] = [];

            for (const name of names) {
                promises.push(
                    this.api().upload(`${this.resourceType.toLowerCase()}/file?name=${name}`, {
                        file: files[name],
                    })
                );
            }

            await Promise.allSettled(promises);

            dispatch(this.clear());
            dispatch(this.load());
        };
    }

    public uploadImages(files: {[name: string]: {file: File, checkAlphaChannel?: boolean}}): DispatchResponse {
        return async (dispatch: DispatchFunc) => {
            const names: string[] = Object.keys(files);

            const promises: Promise<any>[] = [];

            for (const name of names) {
                const {file, checkAlphaChannel} = files[name];

                promises.push(
                    this.api().upload(`${this.resourceType.toLowerCase()}/image?name=${name}&checkAlphaChannel=${!!checkAlphaChannel}`, {
                        file,
                    })
                );
            }

            const validationErrors: {[field: string]: string} = {};

            await Promise.allSettled(promises)
                .then((results: PromiseSettledResult<any>[]) => {
                    results.forEach((result: PromiseSettledResult<any>): void => {
                        if (result.status === 'rejected') {
                            if (result.reason.validation) {
                                const fieldName: string = Object.keys(result.reason.validation)[0];
                                validationErrors[fieldName] = result.reason.validation[fieldName];
                            }
                        }
                    });
                });

            if (Object.keys(validationErrors).length) {
                dispatch({type: `${this.resourceType}_UPLOAD_FAILED`, error: {validation: validationErrors}});
            }

            dispatch(this.clear());
            dispatch(this.load());
        };
    }

    public getThemeParameter(application: ApplicationType, parameter: string, defaultValue?: any): any {
        if (undefined === this.playerSettings?.theme ||
			undefined === this.playerSettings?.theme[application] ||
			undefined === this.playerSettings?.theme[application][parameter]) {
            return defaultValue;
        }

        return this.playerSettings.theme[application][parameter];
    }

    public getImage(name?: string): string {
        return super.getImage(defaultImage, name);
    }

    public deleteFile(name: string = 'file'): DispatchResponse {
        return (dispatch: DispatchFunc): void => {
            this.api().delete(`${this.resourceType.toLowerCase()}/file?name=${name}`).then(() => {
                dispatch(this.clear());
                dispatch(this.load());
            }).catch(() => null);
        };
    }

    public getConfirmConfigurationWarning(t: TFunction, type: PluginSubType): string {
        const form: Form = forms
            .getItems()
            .find(((form: Form) => form.type === FormType.CONFIRM));

        const formFieldType: FormFieldType = type === PluginSubType.SMTP
            ? FormFieldType.EMAIL_VERIFICATION_TOKEN
            : type === PluginSubType.SMS
                ? FormFieldType.PHONE_VERIFICATION_TOKEN
                : null;

        if (undefined === form || undefined == form.fields.find((field: FormField): boolean => field.type === formFieldType)) {
            return t('account.confirmationFormNotConfigured');
        }

        const pluginsIds: Uid[] = plugins
            .getItems()
            .filter((plugin: Plugin) => plugin.getSubType() === type)
            .map((plugin: Plugin) => plugin.id);

        const activePluginInstances: PluginInstance[] =  pluginInstances
            .getItems()
            .filter((instance: PluginInstance) =>
                pluginsIds.includes(instance.pluginId) && instance.active,
            );

        if (!activePluginInstances.length) {
            return t(`account.${type}PluginNotConfigured`);
        }

        const template: Template = templates
            .getItems()
            .find((template: Template) => {
                if (template.type === TemplateType.CONFIRMATION) {
                    return undefined !==  activePluginInstances.find((pluginInstance: PluginInstance) =>
                        pluginInstance.id === template.pluginInstanceId,
                    );
                }
            });

        if (undefined === template) {
            return t('account.noConfirmationTemplate');
        }

        return null;
    }

    public static showDaysForDropDown(t: TFunction): DropDownItem[] {
        const result: DropDownItem[] = [];

        result.push({
            label: t('account.doNotShow'),
            value: 0,
        });

        for (let i = 1; i <= 30; i++) {
            result.push({
                label: `${i}`,
                value: i,
            });
        }

        return result;
    }

    public static maxSubscriberProfilesForDropDown(empty?: string): DropDownItem[] {
        const result: DropDownItem[] = [];

        if (empty) {
            result.push({
                label: empty,
                value: null,
            });
        }

        for (let i: number = 1; i <= 9; i++) {
            result.push({
                label: `${i}`,
                value: i,
            });
        }

        return result;
    }

    public static tvSafeAreaOffsetsForDropDown(): DropDownItem[] {
        const result: DropDownItem[] = [];

        for (let offset: number = 0.5; offset <= 3; offset += 0.5) {
            result.push({
                label: `${offset}%`,
                value: offset,
            });
        }

        return result;
    }

    public static concurrentConnectionsOnlyFromOneIpForDropDown(t: TFunction, empty?: string): DropDownItem[] {
        const result: DropDownItem[] = [];

        if (empty) {
            result.push({
                label: empty,
                value: null,
            });
        }

        result.push(
            {label: t('operatorSettings.restrictIp'), value: true},
            {label: t('operatorSettings.noIpRestriction'), value: false},
        );

        return result;
    }

    public static adultContentVisibilityForDropDown(t: TFunction): DropDownItem[] {
        return [
            {
                label: t('operator.showAdultContent'),
                value: AdultContentVisibility.SHOW,
            },
            {
                label: t('operator.hideAdultContent'),
                value: AdultContentVisibility.HIDE,
            },
        ];
    }

    public static parentalPinLifetimeInMinutesForDropDown(t: TFunction): DropDownItem[] {
        return [15, 30, 45, 60].map((minutes: number): DropDownItem => ({
            label: t('operator.minutesShort', {value: minutes}),
            value: minutes,
        }));
    }
}

export {Operator};

export default new Operator({}, null);
